import { useMemo, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemIcon,
  ListItemText,
  debounce,
} from '@mui/material';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { EmailTemplateSelectFragment } from '@graphql/__generated__/graphql';
import {
  RaAutoComplete,
  type RaAutoCompleteCreatableItem,
  isCreatableItem,
} from '@modules/_shared/components/data-grid/RaAutoComplete';
import { useLocale } from '@shared/hooks/locale';

type EmailTemplatesDialogProps = {
  open: boolean;
  onClose: () => void;
  onTemplateSelect: (template: EmailTemplateSelectFragment) => void;
};

export const SEARCH_TEMPLATES_SELECT_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailTemplateSelect on email_templates {
    id
    name
    subject
    subject_plaintext
    text
    add_signature
    cc
    bcc
    email_templates_files {
      file {
        id
        name
        url
        size
        mimetype
      }
    }
  }
`);

const SEARCH_TEMPLATES = gql(/* GraphQL */ `
  query SearchEmailTemplates($search: String!) {
    email_templates(
      where: {
        _or: [{ name: { _ilike: $search } }, { subject: { _ilike: $search } }]
        lot_id: { _is_null: true }
        development_id: { _is_null: true }
      }
      limit: 20
    ) {
      id
      ...EmailTemplateSelect
    }
  }
`);

const TemplatesList: React.FC<{
  onTemplateSelect: (template: EmailTemplateSelectFragment) => void;
  onNewTemplateSelected: () => void;
}> = ({ onTemplateSelect, onNewTemplateSelected }) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<EmailTemplateSelectFragment[]>([]);
  const autocompleteRef = useRef<HTMLDivElement | null>(null);

  const [searchTemplates, { loading }] = useLazyQuery(SEARCH_TEMPLATES, {
    notifyOnNetworkStatusChange: true,
  });

  const debouncedSearch = useMemo(
    () =>
      debounce(value => {
        searchTemplates({
          variables: {
            search: `%${value}%`,
          },
        }).then(response => {
          setOpen(true);
          setOptions(response.data?.email_templates ?? []);
        });
      }, 275),
    [searchTemplates],
  );

  return (
    <RaAutoComplete<EmailTemplateSelectFragment>
      InputProps={{
        placeholder: t('Search templates'),
      }}
      renderOption={(
        props: React.HTMLAttributes<HTMLLIElement>,
        option:
          | EmailTemplateSelectFragment
          | RaAutoCompleteCreatableItem<EmailTemplateSelectFragment>,
      ) =>
        isCreatableItem(option) ? (
          <ListItem {...props} key="new-template">
            <ListItemIcon>
              <Plus size={20} />
            </ListItemIcon>
            <ListItemText
              primary={t('Create template {{name}}', {
                name: option.inputValue,
              })}
            />
          </ListItem>
        ) : (
          <ListItem {...props} key={option.id}>
            <ListItemText
              primary={option.name}
              secondary={option.subject_plaintext}
            />
          </ListItem>
        )
      }
      onChange={async (
        _: any,
        value:
          | EmailTemplateSelectFragment
          | RaAutoCompleteCreatableItem<EmailTemplateSelectFragment>
          | null,
      ) => {
        if (value != null && isCreatableItem(value)) {
          onNewTemplateSelected();
        } else if (value != null) {
          onTemplateSelect(value);
        }
      }}
      onFocus={() => {
        debouncedSearch('');
      }}
      getOptionLabel={option => option.name || ''}
      options={options}
      loading={loading}
      open={open}
      onClose={() => {
        setOpen(false);
        setOptions([]);
      }}
      autoFocus={true}
      debouncedSearch={debouncedSearch}
      setOpen={setOpen}
      setOptions={setOptions}
      ref={autocompleteRef}
      creatable={true}
    />
  );
};

export const EmailTemplatesDialog: React.FC<EmailTemplatesDialogProps> = ({
  onClose,
  onTemplateSelect,
  open,
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('Select template')}</DialogTitle>
      <DialogContent>
        <TemplatesList
          onTemplateSelect={onTemplateSelect}
          onNewTemplateSelected={() => {
            navigate('/emails/templates/add');
            onClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
};
